<script setup lang="ts">
  import {
    type TableDefaultColumnOptions,
    VButton,
    VCheckBox,
    VPopover,
    VSkeleton,
  } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed, onMounted, ref, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRoute } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import {
    TARGET_BY_VALUE,
    formatComparisonPeriod,
    loadTargetItems,
    useComparisonStore,
  } from "@/entities/comparison";
  import {
    DEFAULT_FILTERS,
    INDICATOR_OPTIONS,
    REPORT_STRING_GROUPS,
    REPORT_STRING_OPTION_BY_KEY,
    buildPreviousPeriodBody,
    buildRequestStatisticsBody,
    compareReportIndicators,
    dateFormatForSection,
    fetchReport,
    formatIndicatorValue,
    parseIndicatorValue,
    responseRowsToReportRows,
  } from "@/entities/report";
  import type { PeriodType, ReportFilters, ReportResponse } from "@/entities/report";
  import { message$ } from "@/entities/tech";
  import Sheet from "@/components/atoms/Sheet.vue";
  import ChartColumn from "@/components/icons/ChartColumn.vue";
  import ChevronRight from "@/components/icons/ChevronRight.vue";
  import Ellipsis from "@/components/icons/Ellipsis.vue";
  import Star from "@/components/icons/Star.vue";
  import IndicatorCard from "@/components/molecules/IndicatorCard.vue";
  import PickerButton from "@/components/molecules/PickerButton.vue";
  import TableSkeleton from "@/components/molecules/TableSkeleton.vue";
  import { Table } from "@/components/organisms/Table";
  import type { ITableColumn } from "@/components/organisms/Table/table.types";
  import { downloadCsv } from "@/lib/csv";
  import type { CsvValue } from "@/lib/csv";

  const { t } = useI18n();
  const route = useRoute();
  const comparisonStore = useComparisonStore();

  // Date-based sections read/format the `date` field; everything else uses `name`.
  const DATE_SECTIONS = new Set([2, 8, 9, 10]);

  const recordId = computed(() => Number(route.params.id));
  const record = computed(() => comparisonStore.get(recordId.value) ?? null);

  onMounted(() => void comparisonStore.load());

  // --- Header -------------------------------------------------------------
  const periodLabel = computed(() =>
    record.value ? formatComparisonPeriod(record.value.period) : "",
  );
  const subtitle = computed(() => {
    const value = record.value;
    if (!value) return "";
    const versus =
      value.targetValueTwo ||
      t("page.factorComparison.previousPeriod", {}, { default: "Прошлый период" });
    return `${value.targetValueOne} vs ${versus} • ${periodLabel.value}`;
  });

  // --- Section ("Разрез") picker -----------------------------------------
  const selectedSection = ref<number>(6);

  const stringGroups = computed(() =>
    REPORT_STRING_GROUPS.map((group) => ({
      title: t(group.title.path, {}, { default: group.title.default }),
      options: group.options.map((option) => ({
        key: option.key,
        label: t(option.title.path, {}, { default: option.title.default }),
      })),
    })),
  );
  const sectionLabel = computed(() => {
    const option = REPORT_STRING_OPTION_BY_KEY[selectedSection.value];
    return option
      ? t(option.title.path, {}, { default: option.title.default })
      : t("page.report.columns.date", {}, { default: "Дата" });
  });
  const groupingKey = computed<"name" | "date">(() =>
    DATE_SECTIONS.has(selectedSection.value) ? "date" : "name",
  );
  // Grouping-cell date format follows the section: months show "MM.YYYY".
  const groupingDateFormat = computed(() => dateFormatForSection(selectedSection.value));
  // Rows are three times the single-line height across every section, so the
  // grouping cell can wrap onto three lines.
  const BASE_ROW_HEIGHT = 33;
  const rowHeight = BASE_ROW_HEIGHT * 3;

  function selectSection(key: number) {
    selectedSection.value = key;
  }

  // --- Display settings (KPI cards / table columns) -----------------------
  const cardIndicators = ref<Set<number>>(new Set());
  const tableIndicators = ref<Set<number>>(new Set());
  // Track whether the visibility sets have been seeded yet, so we don't clobber
  // an (intentionally) empty selection on a reactive record update.
  let displaySeeded = false;
  // Seed visibility from the saved `display` settings, falling back to the
  // comparison's chosen indicators (which become visible, the rest hidden).
  watch(
    record,
    (value) => {
      if (!value || displaySeeded) return;
      cardIndicators.value = new Set(value.display?.cards ?? value.indicators);
      tableIndicators.value = new Set(value.display?.columns ?? value.indicators);
      displaySeeded = true;
    },
    { immediate: true },
  );

  type SettingsSheet = "cards" | "columns";
  const settingsOpen = ref(false);
  const activeSheet = ref<SettingsSheet | null>(null);
  // Draft edited inside the open sheet; only committed to the live set on Apply.
  const draftIndicators = ref<Set<number>>(new Set());

  const SETTINGS_ITEMS: { key: SettingsSheet; label: string; icon: typeof Star }[] = [
    {
      key: "cards",
      label: t("page.factorComparison.settings.cards", {}, { default: "KPI-карточки" }),
      icon: Star,
    },
    {
      key: "columns",
      label: t("page.factorComparison.settings.columns", {}, { default: "Столбцы таблицы" }),
      icon: ChartColumn,
    },
  ];

  function openSheet(key: SettingsSheet) {
    settingsOpen.value = false;
    activeSheet.value = key;
    // Start the draft from the current visible set.
    draftIndicators.value = new Set(key === "cards" ? cardIndicators.value : tableIndicators.value);
  }

  const activeSheetTitle = computed(
    () => SETTINGS_ITEMS.find((item) => item.key === activeSheet.value)?.label ?? "",
  );

  function toggleDraftIndicator(key: number) {
    const next = new Set(draftIndicators.value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    draftIndicators.value = next;
  }

  // Persist the current card/column visibility to IndexedDB.
  function persistDisplay() {
    if (Number.isNaN(recordId.value)) return;
    void comparisonStore.update(recordId.value, {
      display: { cards: [...cardIndicators.value], columns: [...tableIndicators.value] },
    });
  }

  // Apply the draft to the live set, save it, and close the sheet.
  function applySettings() {
    if (activeSheet.value === "cards") cardIndicators.value = new Set(draftIndicators.value);
    else if (activeSheet.value === "columns")
      tableIndicators.value = new Set(draftIndicators.value);
    persistDisplay();
    activeSheet.value = null;
  }

  // Hide an indicator's columns from the table (the eye button in any of its
  // four headers removes all four), and persist the change.
  function hideIndicator(key: number) {
    if (!tableIndicators.value.has(key)) return;
    const next = new Set(tableIndicators.value);
    next.delete(key);
    tableIndicators.value = next;
    persistDisplay();
  }

  const allIndicators = computed(() =>
    INDICATOR_OPTIONS.map((option) => ({
      key: option.key,
      label: t(option.title.path, {}, { default: option.title.default }),
    })),
  );

  // Selected option objects, in canonical order, for table columns / cards.
  const tableIndicatorOptions = computed(() =>
    INDICATOR_OPTIONS.filter((option) => tableIndicators.value.has(option.key)),
  );
  const cardIndicatorOptions = computed(() =>
    INDICATOR_OPTIONS.filter((option) => cardIndicators.value.has(option.key)),
  );

  // --- Resolve the target's selected object ids (names → ids) -------------
  const targetItems = ref<{ id: number; name: string }[]>([]);
  // Gate the report requests until the target list has resolved, so the bodies
  // are built once (with the target filter) rather than firing an unfiltered
  // request first and a filtered one after the names → ids map arrives.
  const targetsLoaded = ref(false);
  watch(
    record,
    async (value) => {
      if (!value) return;
      targetsLoaded.value = false;
      try {
        targetItems.value = await loadTargetItems(TARGET_BY_VALUE[value.target]);
      } catch {
        targetItems.value = [];
      } finally {
        targetsLoaded.value = true;
      }
    },
    { immediate: true },
  );
  const idByName = computed(() => new Map(targetItems.value.map((item) => [item.name, item.id])));
  const idOne = computed(() =>
    record.value ? (idByName.value.get(record.value.targetValueOne) ?? null) : null,
  );
  const idTwo = computed(() =>
    record.value ? (idByName.value.get(record.value.targetValueTwo) ?? null) : null,
  );

  // --- Request bodies for the two compared datasets -----------------------
  // Base filters shared by both requests: the comparison's period, the chosen
  // section, and every indicator (so any card/column toggle has data).
  const baseFilters = computed<ReportFilters | null>(() => {
    if (!record.value) return null;
    return {
      ...DEFAULT_FILTERS,
      period: record.value.period as PeriodType,
      section: selectedSection.value,
      indicators: INDICATOR_OPTIONS.map((option) => option.key),
      attribution: 1,
    };
  });

  function withTarget(filters: ReportFilters, id: number | null): ReportFilters {
    if (id === null || !record.value) return filters;
    return { ...filters, [TARGET_BY_VALUE[record.value.target].filterField]: [id] };
  }

  const bodyP1 = computed(() => {
    if (!baseFilters.value || !targetsLoaded.value) return null;
    return buildRequestStatisticsBody(withTarget(baseFilters.value, idOne.value));
  });
  const bodyP2 = computed(() => {
    if (!baseFilters.value || !record.value || !targetsLoaded.value) return null;
    if (record.value.mode === "previous_period") {
      return bodyP1.value ? buildPreviousPeriodBody(bodyP1.value) : null;
    }
    return buildRequestStatisticsBody(withTarget(baseFilters.value, idTwo.value));
  });

  // Surface a request failure as an error message — but stay silent when the
  // app is offline (the cache layer already shows the offline notice). Deduped
  // so a simultaneous P1+P2 failure shows a single toast.
  let errorNotified = false;
  function handleRequestError() {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    if (errorNotified) return;
    errorNotified = true;
    void Promise.resolve().then(() => {
      errorNotified = false;
    });
    message$.next({
      type: "error",
      text: t(
        "page.factorComparison.requestError",
        {},
        { default: "Произошла ошибка запроса на получение данных, попробуйте позже." },
      ),
    });
  }

  const { data: dataP1, isLoading: loadingP1 } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: computed(() => (bodyP1.value ? `factor-p1-${JSON.stringify(bodyP1.value)}` : "")),
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      if (!bodyP1.value) throw new Error("missing P1 request body");
      return fetchReport(bodyP1.value);
    },
    onError: handleRequestError,
  });

  const { data: dataP2, isLoading: loadingP2 } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: computed(() => (bodyP2.value ? `factor-p2-${JSON.stringify(bodyP2.value)}` : "")),
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      if (!bodyP2.value) throw new Error("missing P2 request body");
      return fetchReport(bodyP2.value);
    },
    onError: handleRequestError,
  });

  const isLoading = computed(
    () => (loadingP1.value && !dataP1.value) || (loadingP2.value && !dataP2.value),
  );

  // Percent change of P1 against P2 (same convention as compareReportIndicators).
  function diffPercent(p1: number, p2: number): number {
    if (p2 === 0) return p1 === 0 ? 0 : 100;
    return Math.round(((p1 - p2) / p2) * 10000) / 100;
  }

  // Empty response stand-in so a missing object is compared as all-zeroes
  // (compareReportIndicators only reads `statistics`).
  const EMPTY_RESPONSE = { statistics: [] } as unknown as ReportResponse;

  // --- KPI cards ----------------------------------------------------------
  // When only one object has data, the other side is counted as 0 (and vice
  // versa); only when both are missing is the comparison empty.
  const comparison = computed(() => {
    if (!dataP1.value && !dataP2.value) return {};
    return compareReportIndicators(dataP1.value ?? EMPTY_RESPONSE, dataP2.value ?? EMPTY_RESPONSE);
  });
  const cards = computed(() =>
    cardIndicatorOptions.value.map((option) => {
      const totals = comparison.value[option.name];
      const format = "format" in option ? option.format : undefined;
      return {
        key: option.key,
        name: t(option.title.path, {}, { default: option.title.default }),
        value: formatIndicatorValue(totals?.firstValue ?? 0, format),
        percent: totals?.diffPercent,
      };
    }),
  );

  // --- Table --------------------------------------------------------------
  type FactorRow = Record<string, unknown>;

  const p1Rows = computed(
    () => responseRowsToReportRows(dataP1.value?.statistics ?? []) as Record<string, unknown>[],
  );
  const p2Rows = computed(
    () => responseRowsToReportRows(dataP2.value?.statistics ?? []) as Record<string, unknown>[],
  );

  // The field that uniquely identifies a grouping row for the current section.
  // Date sections (e.g. "По дням") carry no usable `id` — their unique value is
  // the `date` itself — so we key by the grouping value there and by `id`
  // elsewhere. This also keeps the table's `getRowId` unique (a shared `id: 0`
  // across date rows would otherwise collapse them).
  function rowJoinKey(row: Record<string, unknown>, key: "name" | "date"): string {
    return key === "date" ? String(row.date) : String(row.id);
  }

  // Fill the four per-indicator cells (P1, P2, Δ, Δ%) for a paired row.
  function fillIndicatorCells(
    out: FactorRow,
    rowP1: Record<string, unknown> | undefined,
    rowP2: Record<string, unknown> | undefined,
  ) {
    for (const option of tableIndicatorOptions.value) {
      const p1 = parseIndicatorValue(rowP1?.[option.name]);
      const p2 = parseIndicatorValue(rowP2?.[option.name]);
      out[`${option.name}_p1`] = p1;
      out[`${option.name}_p2`] = p2;
      out[`${option.name}_total`] = p1 - p2;
      out[`${option.name}_procent`] = diffPercent(p1, p2);
    }
  }

  const isPreviousPeriod = computed(() => record.value?.mode === "previous_period");

  const rows = computed<FactorRow[]>(() => {
    const key = groupingKey.value;

    // Previous-period mode over a date section: P1 and P2 cover different but
    // equal-length date ranges, so there's no shared date to join on. Compare
    // the days positionally instead — the i-th day of the current period against
    // the i-th day of the previous one — labelling each row with the current
    // period's date.
    if (key === "date" && isPreviousPeriod.value) {
      const count = Math.max(p1Rows.value.length, p2Rows.value.length);
      return Array.from({ length: count }, (_unused, index) => {
        const rowP1 = p1Rows.value[index];
        const rowP2 = p2Rows.value[index];
        // Row id is the position (unique); the displayed date is the current
        // period's day, falling back to the previous period's when P1 is short.
        // Date rows carry no entity id, so `groupId` stays absent (no prefix).
        const out: FactorRow = {
          id: index,
          groupId: (rowP1 ?? rowP2)?.id,
          [key]: (rowP1 ?? rowP2)?.[key],
        };
        fillIndicatorCells(out, rowP1, rowP2);
        return out;
      });
    }

    const p1ById = new Map(p1Rows.value.map((row) => [rowJoinKey(row, key), row]));
    const p2ById = new Map(p2Rows.value.map((row) => [rowJoinKey(row, key), row]));

    // Union of rows by join key, so a row present in only one object still shows
    // with the missing side counted as 0. P1 order first, then P2-only rows.
    const orderedIds = p1Rows.value.map((row) => rowJoinKey(row, key));
    for (const row of p2Rows.value) {
      const id = rowJoinKey(row, key);
      if (!p1ById.has(id)) orderedIds.push(id);
    }

    return orderedIds.map((joinKey, index) => {
      const rowP1 = p1ById.get(joinKey);
      const rowP2 = p2ById.get(joinKey);
      const source = rowP1 ?? rowP2;
      // Use the join key as the row id so every row is unique for the table,
      // even when the backend omits `id` on date-grouped rows. `groupId` holds
      // the entity id (when any) so the cell can prefix it.
      const out: FactorRow = { id: joinKey || index, groupId: source?.id, [key]: source?.[key] };
      fillIndicatorCells(out, rowP1, rowP2);
      return out;
    });
  });

  // When both compared objects return no rows (once both requests have settled),
  // notify the user.
  let noDataNotified = false;
  watch([dataP1, dataP2, loadingP1, loadingP2], () => {
    if (loadingP1.value || loadingP2.value) return;
    if (dataP1.value === null || dataP2.value === null) return;
    if (p1Rows.value.length > 0 || p2Rows.value.length > 0) {
      noDataNotified = false;
      return;
    }
    if (noDataNotified) return;
    noDataNotified = true;
    message$.next({
      type: "warning",
      text: t(
        "page.factorComparison.noData",
        {},
        { default: "Данные по сравниваемым объектам отсутствуют" },
      ),
    });
  });

  const DEFAULT_COLUMN: TableDefaultColumnOptions = {
    draggable: false,
    sortable: false,
    filterable: false,
    resizable: false,
    cellClass: ["common", "hCenter"],
    headerClass: ["common"],
  };

  function getRowId(row: FactorRow, index: number) {
    const id = row.id;
    return typeof id === "number" || typeof id === "string" ? String(id) : String(index);
  }

  // First, frozen column — the section value (no expandable drill-down here).
  const groupingColumn = computed<ITableColumn<FactorRow>>(() => {
    const key = groupingKey.value;
    return {
      key,
      name: sectionLabel.value,
      minWidth: 200,
      maxWidth: 200,
      sortable: false,
      leftFrozen: true,
      // Single-line cell with ellipsis when the grouping value overflows.
      cellRender: {
        component: "grouping",
        props: {
          field: key,
          isDate: key === "date",
          dateFormat: groupingDateFormat.value,
          // Non-date sections prefix the entity id and wrap onto three lines.
          idField: key === "date" ? undefined : "groupId",
          multiline: key !== "date",
        },
      },
      // Pin/unpin toggle in the header to freeze the first column.
      headerRender: { component: "fixable" },
    };
  });

  // Four columns per selected indicator: P1, P2, Δ, Δ%.
  const indicatorColumns = computed<ITableColumn<FactorRow>[]>(() =>
    tableIndicatorOptions.value.flatMap((option) => {
      const label = t(option.title.path, {}, { default: option.title.default });
      const format = "format" in option ? option.format : undefined;
      // The eye button in any column header hides the whole indicator.
      const headerRender = {
        component: "hideable" as const,
        props: { onHide: () => hideIndicator(option.key) },
      };
      // P1 / P2 — plain formatted values.
      const valueColumn = (suffix: "p1" | "p2", name: string): ITableColumn<FactorRow> => ({
        key: `${option.name}_${suffix}`,
        name,
        minWidth: 160,
        maxWidth: 220,
        headerRender,
        cellRender: {
          component: "default",
          props: {
            customContent: (context) =>
              formatIndicatorValue(context.row.original[`${option.name}_${suffix}`], format),
          },
        },
      });
      // Δ / Δ% — green when positive, red when negative.
      const deltaColumn = (suffix: "total" | "procent", name: string): ITableColumn<FactorRow> => ({
        key: `${option.name}_${suffix}`,
        name,
        minWidth: 160,
        maxWidth: 220,
        headerRender,
        cellRender: {
          component: "delta",
          props: {
            field: `${option.name}_${suffix}`,
            format,
            percent: suffix === "procent",
          },
        },
      });
      return [
        valueColumn("p1", `${label} P1`),
        valueColumn("p2", `${label} P2`),
        deltaColumn("total", `Δ${label}`),
        deltaColumn("procent", `Δ ${label} %`),
      ];
    }),
  );

  const columns = computed<ITableColumn<FactorRow>[]>(() => [
    groupingColumn.value,
    ...indicatorColumns.value,
  ]);

  // Keep the KPI block's height while loading; fall back to a sensible default
  // before the card selection resolves.
  const cardSkeletonCount = computed(() => cardIndicatorOptions.value.length || 6);
  // Per-column widths so the table skeleton mirrors the real layout.
  const skeletonColumns = computed(() => columns.value.map((column) => column.minWidth ?? 120));

  // --- CSV export ---------------------------------------------------------
  // The grouping cell keeps its display text (date-formatted); indicator cells
  // export their raw numeric values.
  function csvCell(key: string, value: unknown): CsvValue {
    if (key === groupingKey.value) {
      if (groupingKey.value === "date" && typeof value === "string") {
        const parsed = dayjs(value);
        if (parsed.isValid()) return parsed.format(groupingDateFormat.value);
      }
      return typeof value === "string" || typeof value === "number" ? value : "";
    }
    return typeof value === "number" ? value : "";
  }

  const exportFileName = computed(() => {
    const value = record.value;
    if (!value) return "factor-comparison";
    const versus = value.targetValueTwo || "previous";
    return `${value.targetValueOne}-vs-${versus}`.replace(/\s+/g, "_");
  });

  const exportMenuOpen = ref(false);

  function exportCsv() {
    exportMenuOpen.value = false;
    if (rows.value.length === 0) return;
    const cols = columns.value;
    const header = cols.map((column) => column.name);
    const body = rows.value.map((row) =>
      cols.map((column) => csvCell(String(column.key), row[String(column.key)])),
    );
    downloadCsv(exportFileName.value, [header, ...body]);
  }
</script>

<template>
  <div :class="$style.base">
    <section :class="$style.header">
      <div :class="$style.titleArea">
        <h1 :class="$style.title">
          {{ $t("page.factorComparison.title", {}, { default: "Факторный анализ" }) }}
        </h1>
        <VPopover v-model="exportMenuOpen" :class-name-positioner-content="$style.menu">
          <VButton><Ellipsis /></VButton>
          <template #content>
            <button
              type="button"
              :class="$style.menuItem"
              :disabled="!rows.length"
              @click="exportCsv"
            >
              {{ $t("page.factorComparison.exportCsv", {}, { default: "Экспорт CSV" }) }}
            </button>
          </template>
        </VPopover>
      </div>
      <span :class="$style.subtitle">{{ subtitle }}</span>
    </section>

    <section :class="$style.cards">
      <template v-if="isLoading">
        <div
          v-for="n in cardSkeletonCount"
          :key="`card-skeleton-${n}`"
          :class="$style.cardSkeleton"
          aria-hidden="true"
        >
          <VSkeleton type="text" :animation="true" :class="$style.cardSkeletonName" />
          <VSkeleton type="text" :animation="true" :class="$style.cardSkeletonValue" />
        </div>
      </template>
      <IndicatorCard
        v-for="card in cards"
        v-else
        :key="card.key"
        :name="card.name"
        :value="card.value"
        :percent="card.percent"
      />
    </section>

    <section :class="$style.toolbar">
      <PickerButton
        :value="sectionLabel"
        :placeholder="$t('page.report.section.placeholder', {}, { default: 'Разрез' })"
        :title="$t('page.report.section.title', {}, { default: 'Разрез' })"
        mode="sheet"
      >
        <div :class="$style.breakdownGroups">
          <div v-for="group in stringGroups" :key="group.title" :class="$style.breakdownGroup">
            <span :class="$style.breakdownGroupLabel">{{ group.title }}</span>
            <ul :class="$style.breakdownList">
              <li
                v-for="option in group.options"
                :key="option.key"
                :class="$style.breakdownItem"
                @click="selectSection(option.key)"
              >
                <span>{{ option.label }}</span>
                <span
                  v-if="selectedSection === option.key"
                  :class="$style.activeDot"
                  aria-hidden="true"
                />
              </li>
            </ul>
          </div>
        </div>
      </PickerButton>

      <div :class="$style.actions">
        <VPopover v-model="settingsOpen" :class-name-positioner-content="$style.settingsMenu">
          <VButton type="text">
            {{
              $t("page.factorComparison.settings.button", {}, { default: "Настройки отображения" })
            }}
          </VButton>
          <template #content>
            <button
              v-for="item in SETTINGS_ITEMS"
              :key="item.key"
              type="button"
              :class="$style.settingsItem"
              @click="openSheet(item.key)"
            >
              <component :is="item.icon" :size="16" :class="$style.settingsItemIcon" />
              <span :class="$style.settingsItemLabel">{{ item.label }}</span>
              <ChevronRight :size="16" />
            </button>
          </template>
        </VPopover>
      </div>
    </section>

    <section :class="$style.table">
      <TableSkeleton
        v-if="isLoading"
        :columns="skeletonColumns"
        :header-height="60"
        :row-height="rowHeight"
      />
      <Table
        v-else
        :rows="rows"
        :columns="columns"
        :default-column-options="DEFAULT_COLUMN"
        :get-row-id="getRowId"
        :virtual-rows="true"
        :virtual-row-size="rowHeight"
        :header-height="60"
        :row-height="rowHeight"
        :with-total="false"
      />
    </section>
  </div>

  <Sheet :open="activeSheet !== null" :title="activeSheetTitle" @update:open="activeSheet = null">
    <div :class="$style.indicatorSheetContent">
      <div :class="$style.indicatorList">
        <div
          v-for="indicator in allIndicators"
          :key="indicator.key"
          :class="$style.indicatorRow"
          @click="toggleDraftIndicator(indicator.key)"
        >
          <VCheckBox
            :model-value="draftIndicators.has(indicator.key)"
            @click.stop
            @update:model-value="toggleDraftIndicator(indicator.key)"
          >
            {{ indicator.label }}
          </VCheckBox>
        </div>
      </div>
      <div :class="$style.sheetFooter">
        <VButton type="text" @click="activeSheet = null">
          {{ $t("page.factorComparison.settings.cancel", {}, { default: "Отменить" }) }}
        </VButton>
        <VButton type="primary" :class="$style.applyButton" @click="applySettings">
          {{ $t("page.factorComparison.settings.apply", {}, { default: "Применить" }) }}
        </VButton>
      </div>
    </div>
  </Sheet>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 16px 20px;

    overflow: auto;

    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .titleArea {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    margin: 0;

    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .subtitle {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  // Mirrors IndicatorCard's layout (name line + value line) so the skeleton
  // occupies the same footprint as the real cards.
  .cardSkeleton {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
  }

  .cardSkeletonName {
    width: 40%;
    height: 11px;
  }

  .cardSkeletonValue {
    width: 70%;
    height: 1.75rem;
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .settingsMenu {
    min-width: 220px;
    padding: 4px;

    display: flex;
    flex-direction: column;

    background-color: var(--popover);
  }

  .menu {
    min-width: 200px;
    padding: 4px;

    display: flex;
    flex-direction: column;

    background-color: var(--popover);
  }

  .menuItem {
    padding: 8px 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }

    &:disabled {
      color: var(--muted-foreground);
      cursor: not-allowed;
    }
  }

  .settingsItem {
    padding: 10px 12px;

    display: flex;
    align-items: center;
    gap: 10px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .settingsItemIcon {
    flex-shrink: 0;
    color: var(--muted-foreground);
  }

  .settingsItemLabel {
    flex: 1;
    min-width: 0;
  }

  .table {
    max-height: 600px;
    margin-bottom: 40px;
  }

  .breakdownGroups {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .breakdownGroup {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .breakdownGroupLabel {
    color: var(--muted-foreground);
    font-size: 0.75rem;
    line-height: 1rem;
    text-transform: uppercase;
  }

  .breakdownList {
    list-style: none;
    margin: 0;
    padding: 0;

    display: flex;
    flex-direction: column;
  }

  .breakdownItem {
    padding: 10px 4px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    cursor: pointer;

    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .activeDot {
    width: 8px;
    height: 8px;

    border-radius: 999px;
    background-color: var(--brand);
  }

  .indicatorSheetContent {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
  }

  .indicatorList {
    width: 100%;
    height: 100%;
    max-height: 100%;

    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    flex: 1;

    overflow: auto;
  }

  .indicatorRow {
    padding: 0.5rem;

    display: flex;
    align-items: center;

    cursor: pointer;
  }

  // Cancel/apply bar — a separate block after the list (not sticky), so the
  // last list items never scroll underneath it.
  .sheetFooter {
    margin-top: 12px;
    padding-top: 12px;

    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;

    border-top: 1px solid var(--border-subtle);
  }

  .applyButton {
    background: var(--brand);
  }
</style>
